/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import Q from 'q';

import { useParams } from 'js/lib/useRouter';

import {
  Button,
  Grid,
  InlineNotification,
  Link,
  LoadingSection,
  TemporaryDrawer,
  Typography2,
} from '@coursera/cds-core';
import { DownloadIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { ExportDrawer } from 'bundles/enterprise-admin-drawers/components/ExportDrawer';
import { createReportV2 } from 'bundles/enterprise-admin-drawers/utils/EnterpriseEmailReportsAPIUtils';
import type {
  CreateReportScopeKey,
  UsageReportV2Options,
} from 'bundles/enterprise-admin-drawers/utils/EnterpriseEmailReportsAPIUtils';
import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';
import { getSettingsLink } from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import {
  ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN,
  ENTERPRISE_ADMIN_PERMISSION_VERB,
} from 'bundles/enterprise-admin-roles/types';
import NotificationModal from 'bundles/enterprise-ui/components/NotificationModal';

import _t from 'i18n!nls/enterprise-admin-drawers';

import UsageReportScopeSelector from './components/UsageReportScopeSelector';
import type { ExportFilters, ProgramList } from './types';

const styles = {
  loadingSection: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: var(--cds-spacing-200);
  `,
};

enum REPORT_STEPS {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

export type Props = {
  organizationId: string;
  programs: ProgramList;
  onClose: () => void;
  isOpen: boolean;
};

export const UsageReportDrawer = ({ organizationId, programs, onClose, isOpen }: Props) => {
  const { hasPermission } = useEnterpriseAdminPermissions();
  const enterpriseAdminTrackingData = useEnterpriseAdminTrackingData();
  const track = useTracker();
  const params = useParams();
  const { thirdPartySlug } = params;
  const DEFAULT_REPORT_SCOPE = 'allActive';
  const programsWithPermission = programs.filter(
    (program) =>
      hasPermission({
        domain: 'PROGRAM',
        domainId: program.id,
        subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.USAGE_REPORT,
        verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EXPORT,
      }) ||
      hasPermission({
        domain: 'ORGANIZATION',
        domainId: organizationId,
        subdomain: ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.USAGE_REPORT,
        verb: ENTERPRISE_ADMIN_PERMISSION_VERB.EXPORT,
      })
  );
  const initialFilters: ExportFilters = {
    programs: [],
    dateRange: null,
    options: {
      oneCsvPerProgram: false,
      reportScope: DEFAULT_REPORT_SCOPE,
      includeDeletedMembers: true,
    },
  };

  const [reportStep] = useState<REPORT_STEPS>(REPORT_STEPS.INITIAL);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [reportScope, setReportScope] = useState<CreateReportScopeKey>(DEFAULT_REPORT_SCOPE);
  const { HeadingGroup, Content, Actions } = TemporaryDrawer;

  const handleClose = () => {
    onClose();
    setReportScope(DEFAULT_REPORT_SCOPE);
  };

  const handleOnExport = (newFilters: UsageReportV2Options): Q.Promise<void> => {
    //
    // area for improvement: anticipate/handle error conditions, don't expect
    // response to be immediate, ...
    //
    const promise = createReportV2(organizationId, newFilters);

    return Q(promise).then(() => {
      setShowSuccessModal(true);
      const contractFilter = (
        {
          all: 'all_contracts',
          allActive: 'active_billable_contracts',
          allBillable: 'billable_contracts',
        } as const
      )[newFilters.reportScope ?? DEFAULT_REPORT_SCOPE];
      track('export_enterprise_usage_report', {
        enterprise: {
          organizationSlug: thirdPartySlug,
        },
        enterpriseUsageReportDrawer: {
          filters: [
            contractFilter,
            newFilters.programIds?.length === programsWithPermission.length ? 'all_programs' : 'selected_programs',
            newFilters.startDate && newFilters.endDate ? 'selected_time_range' : 'all_time',
            newFilters.oneCsvPerProgram ? 'one_file_per_program' : 'all_programs_in_one_file',
            newFilters.includeDeletedMembers ? 'include_removed_learners' : 'exclude_removed_learners',
          ],
        },
      });
      handleClose();
    });
  };

  if (showSuccessModal)
    return (
      <NotificationModal
        title={_t('Success')}
        description={_t('Your report will be emailed to you within an hour.')}
        isWarning={false}
        onConfirmation={() => setShowSuccessModal(false)}
      />
    );

  const getDrawerBody = () => {
    if (reportStep === REPORT_STEPS.LOADING) {
      return (
        <div css={styles.loadingSection}>
          <LoadingSection label={_t('Processing report...')} />
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {_t(
              'If you’re short on time, feel free to close this popup — we’ll send you the report via email within an hour.'
            )}
          </Typography2>
        </div>
      );
    } else if (reportStep === REPORT_STEPS.ERROR) {
      return <LoadingSection hasError label={_t('Something went wrong. Please try again later.')} />;
    } else if (reportStep === REPORT_STEPS.SUCCESS) {
      return (
        <Grid container justifyContent="center">
          <Grid item>
            <Button variant="ghost" icon={<DownloadIcon size="large" />} iconPosition="before" size="medium">
              {_t('Download Usage Report')}
            </Button>
          </Grid>
        </Grid>
      );
    } else {
      throw new Error('Invalid report step');
    }
  };

  return reportStep === REPORT_STEPS.INITIAL ? (
    <ExportDrawer
      title={_t('Usage Report')}
      isOpen={isOpen}
      filters={initialFilters}
      organizationId={organizationId}
      programs={programsWithPermission}
      onExport={handleOnExport}
      onRequestClose={handleClose}
      reportScope={reportScope}
      extraSelectFilterViewComponent={
        <UsageReportScopeSelector
          handleChange={(newReportScope: CreateReportScopeKey) => setReportScope(newReportScope)}
          reportScope={reportScope}
        />
      }
      extraSelectFilterViewAction={
        <EnterprisePermissionsRequired
          domain="ORGANIZATION"
          domainId={organizationId}
          subdomain={ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.SETTINGS}
          verb={ENTERPRISE_ADMIN_PERMISSION_VERB.EDIT}
        >
          <InlineNotification severity="information" data-testid="schedule-weekly-usage-reports">
            <React.Fragment>
              <Typography2 component="p" variant="bodyPrimary">
                {_t('Your reports will be emailed to you within the hour.')}
              </Typography2>
              <Typography2 component="p" variant="bodyPrimary">
                {_t('Want to schedule reports on a weekly basis?')}{' '}
                <Link
                  href={`${getSettingsLink(params)}#report-schedule`}
                  onClick={() =>
                    track('click_button', {
                      button: {
                        name: 'enterprise_admin_schedule_weekly_reports',
                      },
                      ...enterpriseAdminTrackingData,
                    })
                  }
                  variant="quiet"
                  typographyVariant="bodyPrimary"
                >
                  {_t('Schedule weekly reports')}
                </Link>
              </Typography2>
            </React.Fragment>
          </InlineNotification>
        </EnterprisePermissionsRequired>
      }
    />
  ) : (
    <TemporaryDrawer open={isOpen} anchor="right" onClose={handleClose} sticky="actions">
      <HeadingGroup>{_t('Usage Report')}</HeadingGroup>
      <Content
        css={css`
          margin-top: var(--cds-spacing-600);
        `}
      >
        {getDrawerBody()}
      </Content>
      <Actions>
        <Button variant="secondary" onClick={handleClose}>
          {_t('Close')}
        </Button>
      </Actions>
    </TemporaryDrawer>
  );
};
